import { List, type ListItemProps, ListSubheader, setRef, Skeleton, Stack, styled } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { equals, groupBy, times } from "ramda";
import { isArray, isNilOrEmpty, isNotNilOrEmpty, isPromise } from "ramda-adjunct";
import React, { forwardRef, ReactNode, type Ref, useEffect, useRef } from "react";
import { ListItemWrapper } from "@sinch/components/form/MultilevelSelect/ListItemWrapper";
import { ListColumnProps, RenderedOptions } from "@sinch/components/form/MultilevelSelect/types";

/**
 * Props interface for the ListColumnBase component.
 */
interface ListColumnBaseProps<TValue = any> {
  /** The list of items to display in the column */
  list: ListColumnProps<TValue>["list"];
  /** Callback fired when mouse pointer hovers over a list item, param is index of item in list */
  onMouseOver?: (index: number) => void;
  onItemHover?: (index: number) => void;
  /** Callback fired when a list item is clicked, param is index of item in list */
  onClick?: (index: number) => void;
  /** Callback fired when the selected value changes */
  onChange?: (e: React.UIEvent<HTMLElement>, value: TValue) => void;
  /** CSS class name to apply to the component */
  className?: string;
  /** Index of the currently selected list item */
  selectedIndex?: number | null;
  /** Currently selected value */
  selectedValue: TValue | null;
  /** Function to get additional ListItem props for a value */
  getListItemProps?: (value: TValue) => ListItemProps;
  focusedId?: number | null;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  renderItem?: (item: Pick<RenderedOptions<TValue>, "value" | "label">) => ReactNode;
  atValuePath?: number | null;
}

/**
 * Base component for rendering a column of selectable list items.
 * Used as part of a multi-level select component.
 */
const ListColumnBase = <TValue,>(
  {
    list,
    onMouseOver,
    onItemHover,
    className,
    selectedIndex,
    getListItemProps,
    onChange,
    onClick,
    selectedValue,
    focusedId,
    onKeyDown,
    renderItem,
    atValuePath,
  }: ListColumnBaseProps<TValue>,
  listRef: Ref<HTMLUListElement>
) => {
  const selectedRef = useRef<HTMLLIElement>(null);
  const container = useRef<HTMLDivElement>(null);

  /**
   * Handles click events on list items
   */
  const handleClick = (canChange: boolean) => (e: React.UIEvent<HTMLElement>, val: TValue, index: number) => {
    onClick?.(index);
    if (canChange) {
      onChange?.(e, val);
    }
  };

  // Scroll to the selected item when component mounts
  useEffect(() => {
    if (selectedRef?.current && container.current) {
      scrollIntoViewInContainer(selectedRef.current, container.current);
    }
  }, []);

  let i = 0;
  return (
    <Grid
      ref={container}
      className={className}
      onKeyDown={onKeyDown}
      size="grow"
      sx={{ height: "inherit", overflowY: "auto" }}
    >
      {isPromise(list) && (
        <Stack spacing={1} sx={{ p: 1 }}>
          {times(
            (ix) => (
              <Skeleton key={ix} height={45 - 8} variant="rounded" width="100%" />
            ),
            5
          )}
        </Stack>
      )}
      {isArray(list) && (
        <List ref={listRef} role="listbox">
          {Object.entries(groupBy(({ group }) => group ?? "", list)).map(([group, groupItems]) => (
            <React.Fragment key={group}>
              {isNotNilOrEmpty(group) && (
                <ListSubheader
                  disableGutters
                  disableSticky
                  inset
                  sx={{ px: 1, width: "100%", textAlign: "left", background: "none" }}
                >
                  {group}
                </ListSubheader>
              )}
              {groupItems.map((item) => {
                const { value, label, description, children } = item;
                const ix = i;
                i++;

                return (
                  <ListItemWrapper<TValue>
                    key={ix}
                    ref={(el) => {
                      if (selectedIndex === ix) {
                        setRef(selectedRef, el);
                      }
                    }}
                    description={description}
                    focused={focusedId === ix}
                    hasChildren={isNotNilOrEmpty(children)}
                    hoverState={ix === selectedIndex}
                    isOnValuePath={atValuePath === ix}
                    label={label}
                    listItemProps={{ ...getListItemProps?.(value), tabIndex: ix, role: "option" }}
                    onItemHover={() => onItemHover?.(ix)}
                    onMouseOver={() => onMouseOver?.(ix)}
                    onSelectItem={(e, val) => handleClick(isNilOrEmpty(children))(e, val, ix)}
                    renderItem={renderItem}
                    selected={equals(selectedValue, value)}
                    value={value}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </List>
      )}
    </Grid>
  );
};

/**
 * A styled list column component for use in multi-level selection interfaces.
 * Enhances the base component with additional styling.
 */
export const ListColumn = styled(forwardRef(ListColumnBase))(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderRight: "none",
  },
  "& li.Mui-focused": {
    backgroundColor: "rgba(18, 122, 170, 0.08)",
  },
})) as <TValue>(props: ListColumnBaseProps<TValue> & { ref?: Ref<HTMLUListElement> }) => React.JSX.Element;

function scrollIntoViewInContainer(element: HTMLElement, container: HTMLElement, options: ScrollToOptions = {}) {
  if (!container || container.scrollHeight <= container.clientHeight) {
    // Container is not scrollable, don't scroll
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  // Check if element is outside the visible area of the container
  if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
    // Calculate scroll position
    const scrollTop = element.offsetTop - container.offsetTop - (container.clientHeight - element.offsetHeight) / 2;

    container.scrollTo({
      top: Math.max(0, scrollTop),
      ...options,
      behavior: "instant",
    });
  }
}
